import { useState } from "react";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { openMapPopout } from "../../lib/popout";
import { Plus, DoorOpen, Pin, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";

// GM-only tab strip across the top of the map. Since players can now genuinely be on different
// scenes at once (doors + Free Roam), the GM needs a way to hop between maps to check on everyone
// without dragging the whole table along — that's exactly what "current_scene_id" used to force.
export default function SceneTabBar({ gameId, game, tokens, sidebarCollapsed }) {
  const activeSceneId = useGameStore((s) => s.activeSceneId);
  const setActiveScene = useGameStore((s) => s.setActiveScene);
  const addScene = useGameStore((s) => s.addScene);
  const setGame = useGameStore((s) => s.setGame);
  const [creating, setCreating] = useState(false);
  const scenes = game?.scenes || [];
  // GmSidebar occupies left-4 (collapsed: 40px button, expanded: 320px panel) at this same
  // top edge — offset past whichever state it's in so the two never overlap/intercept clicks.
  const leftOffsetClass = sidebarCollapsed ? "left-16" : "left-[352px]";
  const isScopedGeneral = !!game.my_branch_id;
  const isRaidMasterGm = !!game.raid_id && !isScopedGeneral;
  const holdingSceneId = isScopedGeneral ? game.my_holding_scene_id : (game.shared_waiting_room ? game.shared_waiting_room_scene_id : null);

  const createScene = async () => {
    const name = window.prompt("Name this new map:");
    if (!name?.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/games/${gameId}/scenes`, { name: name.trim() });
      addScene(data);
      setActiveScene(data.id);
    } catch {
      toast.error("Could not create scene");
    } finally {
      setCreating(false);
    }
  };

  const setAsPartyMap = async () => {
    await api.patch(`/games/${gameId}/active-scene`, { scene_id: activeSceneId });
    toast.success("Everyone still on the old map together will now see this one");
  };

  const setAsWaitingRoom = async () => {
    try {
      if (isScopedGeneral) {
        await api.patch(`/raids/${game.raid_id}/branches/${game.my_branch_id}/holding-scene`, { scene_id: activeSceneId });
        setGame({ ...game, my_holding_scene_id: activeSceneId });
        toast.success("This is now your Branch's private Waiting Room — build it to match your campaign's vibe.");
      } else {
        await api.patch(`/raids/${game.raid_id}/shared-waiting-room`, { scene_id: activeSceneId });
        setGame({ ...game, shared_waiting_room_scene_id: activeSceneId });
        toast.success("This is now the Raid's shared Waiting Room.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set the Waiting Room");
    }
  };

  if (scenes.length === 0) return null;

  return (
    <div className={`absolute top-4 ${leftOffsetClass} z-20 flex items-center gap-1.5 max-w-[50vw] overflow-x-auto scrollbar-thin transition-[left] duration-200`} data-testid="scene-tab-bar">
      {scenes.map((sc) => {
        const hasPlayers = tokens?.some?.((t) => t.scene_id === sc.id && t.token_type === "pc");
        return (
          <div
            key={sc.id}
            data-testid={`scene-tab-${sc.id}`}
            role="button"
            tabIndex={0}
            onClick={() => setActiveScene(sc.id)}
            onKeyDown={(e) => e.key === "Enter" && setActiveScene(sc.id)}
            className={`shrink-0 pl-3 pr-1.5 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors duration-200 flex items-center gap-1.5 cursor-pointer ${
              activeSceneId === sc.id ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "bg-black/60 border-white/10 text-[#f1e0c5]/60 hover:border-[#D4AF37]/50"
            }`}
          >
            {sc.id === game.current_scene_id && <Pin className="w-2.5 h-2.5 shrink-0" />}
            {sc.id === holdingSceneId && <Lock data-testid={`scene-tab-${sc.id}-waiting-room`} title="Waiting Room" className="w-2.5 h-2.5 shrink-0 text-[#D4AF37]" />}
            {sc.name}
            {hasPlayers && <span className="w-1.5 h-1.5 rounded-full bg-[#4d7c0f] shrink-0" data-testid={`scene-tab-${sc.id}-has-players`} />}
            <button
              type="button"
              data-testid={`scene-tab-popout-${sc.id}`}
              title="Pop this map out into its own window"
              onClick={(e) => { e.stopPropagation(); if (!openMapPopout(gameId, sc.id)) toast.error("Too many pop-out windows are already open (limit 50)."); }}
              className="hidden md:flex shrink-0 w-5 h-5 rounded-full items-center justify-center opacity-60 hover:opacity-100 hover:bg-white/10 transition-opacity duration-200"
            >
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        );
      })}
      {activeSceneId && activeSceneId !== game.current_scene_id && (
        <button
          data-testid="scene-set-party-map-button"
          onClick={setAsPartyMap}
          title="Make this the table's default/shared map"
          className="shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors duration-200 flex items-center gap-1"
        >
          <DoorOpen className="w-3 h-3" /> Set as Party Map
        </button>
      )}
      {activeSceneId && (isScopedGeneral || (isRaidMasterGm && game.shared_waiting_room)) && activeSceneId !== holdingSceneId && (
        <button
          data-testid="scene-set-waiting-room-button"
          onClick={setAsWaitingRoom}
          title={isScopedGeneral ? "Mark this as your Branch's private Waiting Room" : "Mark this as the Raid's shared Waiting Room"}
          className="shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors duration-200 flex items-center gap-1"
        >
          <Lock className="w-3 h-3" /> Set as Waiting Room
        </button>
      )}
      <button
        data-testid="scene-create-button"
        onClick={createScene}
        disabled={creating}
        className="shrink-0 w-7 h-7 rounded-full bg-black/60 border border-white/10 text-[#f1e0c5]/60 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors duration-200"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
